import { useMemo, useState } from 'react';
import { BiomarkerClassification, type BiomarkerResult } from '@/api/generated-catch-backend/model';
import type { FilterableClassification } from '../components/biomarker-filter/biomarker-filter';

export function useBiomarkerFilter(biomarkers: BiomarkerResult[]) {
  const [selectedFilter, setSelectedFilter] = useState<FilterableClassification | null>(null);

  // Calculate which classifications exist in the biomarkers
  // Note: Critical is now a separate filter, but out_of_range filter includes both out_of_range AND critical
  const availableClassifications = useMemo(() => {
    const classifications = new Set<FilterableClassification>();
    biomarkers.forEach((biomarker) => {
      if (biomarker.classification === BiomarkerClassification.optimal) {
        classifications.add('optimal');
      } else if (biomarker.classification === BiomarkerClassification.normal) {
        classifications.add('normal');
      } else if (biomarker.classification === BiomarkerClassification.out_of_range) {
        classifications.add('out_of_range');
      } else if (biomarker.classification === BiomarkerClassification.critical) {
        classifications.add('critical');
        classifications.add('out_of_range'); // Critical biomarkers also appear in out_of_range filter
      }
    });
    return classifications;
  }, [biomarkers]);

  // Filter biomarkers based on selected filter
  // Note: When filtering by out_of_range, include both out_of_range AND critical biomarkers
  // When filtering by critical, show ONLY critical biomarkers
  const filteredBiomarkers = useMemo(() => {
    if (!selectedFilter) {
      return biomarkers;
    }

    return biomarkers.filter((biomarker) => {
      if (biomarker.classification === BiomarkerClassification.optimal) {
        return selectedFilter === 'optimal';
      } else if (biomarker.classification === BiomarkerClassification.normal) {
        return selectedFilter === 'normal';
      } else if (biomarker.classification === BiomarkerClassification.critical) {
        // Critical biomarkers match both 'critical' and 'out_of_range' filters
        return selectedFilter === 'critical' || selectedFilter === 'out_of_range';
      } else if (biomarker.classification === BiomarkerClassification.out_of_range) {
        // Out of range (non-critical) only matches 'out_of_range' filter
        return selectedFilter === 'out_of_range';
      }
      return false;
    });
  }, [biomarkers, selectedFilter]);

  return {
    selectedFilter,
    setSelectedFilter,
    availableClassifications,
    filteredBiomarkers,
  };
}
